import React, { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import NotificationDiagnostics from "@/components/NotificationDiagnostics";
import { Plus, Trash2, Shield, UserCog, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", can_manage: false, can_view_stats: false, can_view_clients: false, can_view_fees: false, can_view_leads: false });
  const [saving, setSaving] = useState(false);
  const [avatarBust, setAvatarBust] = useState({});
  const [emailEdit, setEmailEdit] = useState(null); // {user_id, name, email}

  const saveEmail = async () => {
    if (!emailEdit?.email?.trim()) { toast.error("Email required"); return; }
    try {
      await api.patch(`/users/${emailEdit.user_id}`, { email: emailEdit.email.trim() });
      toast.success("Email updated");
      setEmailEdit(null);
      load();
    } catch (e) { toast.error(apiErr(e, "Failed to update email")); }
  };

  const uploadAvatar = async (userId, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image exceeds the 5 MB limit"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/users/${userId}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatarBust((b) => ({ ...b, [userId]: Date.now() }));
      toast.success("Photo updated");
    } catch (err) { toast.error(apiErr(err, "Could not upload photo")); }
  };

  const load = useCallback(async () => {
    const res = await api.get("/users");
    setMembers(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      await api.post("/users", { name: form.name, email: form.email, role: "member", can_manage: form.can_manage, can_view_stats: form.can_view_stats, can_view_clients: form.can_view_clients, can_view_fees: form.can_view_fees, can_view_leads: form.can_view_leads });
      toast.success("Team member added");
      setForm({ name: "", email: "", can_manage: false, can_view_stats: false, can_view_clients: false, can_view_fees: false, can_view_leads: false });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErr(e, "Failed to add member"));
    } finally { setSaving(false); }
  };

  const toggleManage = async (m) => {
    try {
      await api.patch(`/users/${m.user_id}`, { can_manage: !m.can_manage });
      load();
    } catch (e) { toast.error(apiErr(e, "Failed to update")); }
  };

  const toggleStats = async (m) => {
    try {
      await api.patch(`/users/${m.user_id}`, { can_view_stats: !m.can_view_stats });
      load();
    } catch (e) { toast.error(apiErr(e, "Failed to update")); }
  };

  const toggleClients = async (m) => {
    try {
      await api.patch(`/users/${m.user_id}`, { can_view_clients: !m.can_view_clients });
      load();
    } catch (e) { toast.error(apiErr(e, "Failed to update")); }
  };

  const toggleFees = async (m) => {
    try {
      await api.patch(`/users/${m.user_id}`, { can_view_fees: !m.can_view_fees });
      load();
    } catch (e) { toast.error(apiErr(e, "Failed to update")); }
  };

  const toggleLeads = async (m) => {
    try {
      await api.patch(`/users/${m.user_id}`, { can_view_leads: !m.can_view_leads });
      load();
    } catch (e) { toast.error(apiErr(e, "Failed to update")); }
  };

  // Case-insensitive match against real (non-placeholder) emails already on the team.
  const existingMatch = form.email.trim()
    ? members.find((m) => (m.email || "").toLowerCase() === form.email.trim().toLowerCase()
        && !(m.email || "").endsWith("@twpco.placeholder"))
    : null;

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Member removed");
      load();
    } catch (e) { toast.error(apiErr(e, "Failed to remove")); }
  };

  const testUsers = members.filter((m) => /@(studio\.test|resend\.dev)$/.test(m.email) && m.user_id !== user.user_id);

  const cleanupTestUsers = async () => {
    try {
      const res = await api.post("/users/cleanup-test");
      toast.success(`Removed ${res.data.removed} test account${res.data.removed === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(apiErr(e, "Cleanup failed")); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1100px]">
      <header className="flex items-start justify-between mb-8 gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-1">Admin</p>
          <h1 className="font-heading text-4xl font-black tracking-tighter">Team & Permissions</h1>
          <p className="text-zinc-500 mt-2 text-sm">Add team members by email and control who can create projects & assign tasks.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-member-button" className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-2">
              <Plus className="h-4 w-4" /> Add Member
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-heading">Add Team Member</DialogTitle>
              <DialogDescription>Invite a teammate by their Google email and set their permissions.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Full Name</label>
                <Input data-testid="member-name-input" value={form.name} className="mt-1.5 rounded-sm"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Google Email</label>
                <Input data-testid="member-email-input" type="email" value={form.email} className="mt-1.5 rounded-sm"
                  placeholder="name@studio.com"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
                {existingMatch && (
                  <p className="mt-1.5 text-xs text-amber-600 flex items-center gap-1" data-testid="email-exists-hint">
                    Already added (any case) — <span className="font-semibold">{existingMatch.name}</span>
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between border border-zinc-200 rounded-sm px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Can manage projects & tasks</p>
                  <p className="text-xs text-zinc-500">Allows creating projects and assigning tasks</p>
                </div>
                <Switch data-testid="member-manage-switch" checked={form.can_manage}
                  onCheckedChange={(v) => setForm({ ...form, can_manage: v })} />
              </div>
              <div className="flex items-center justify-between border border-zinc-200 rounded-sm px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Can view studio stats</p>
                  <p className="text-xs text-zinc-500">Sees dashboard totals & the project breakdown</p>
                </div>
                <Switch data-testid="member-stats-switch" checked={form.can_view_stats}
                  onCheckedChange={(v) => setForm({ ...form, can_view_stats: v })} />
              </div>
              <div className="flex items-center justify-between border border-zinc-200 rounded-sm px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Can view clients</p>
                  <p className="text-xs text-zinc-500">Sees client contacts on projects. Admins always have access; the Clients directory stays admin-only.</p>
                </div>
                <Switch data-testid="member-clients-switch" checked={form.can_view_clients}
                  onCheckedChange={(v) => setForm({ ...form, can_view_clients: v })} />
              </div>
              <div className="flex items-center justify-between border border-zinc-200 rounded-sm px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Can view fees</p>
                  <p className="text-xs text-zinc-500">Sees the Fees tab on projects and the Fees Report. Admins always have access.</p>
                </div>
                <Switch data-testid="member-fees-switch" checked={form.can_view_fees}
                  onCheckedChange={(v) => setForm({ ...form, can_view_fees: v })} />
              </div>
              <div className="flex items-center justify-between border border-zinc-200 rounded-sm px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Can view leads (CRM)</p>
                  <p className="text-xs text-zinc-500">Sees and manages the client-leads pipeline. Admins always have access.</p>
                </div>
                <Switch data-testid="member-leads-switch" checked={form.can_view_leads}
                  onCheckedChange={(v) => setForm({ ...form, can_view_leads: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-member-button" onClick={add} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm">
                {saving ? "Adding…" : "Add Member"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      {testUsers.length > 0 && (
        <div data-testid="test-users-banner" className="flex flex-wrap items-center justify-between gap-3 border border-amber-300 bg-amber-50 px-5 py-3.5 mb-4">
          <p className="text-sm text-amber-900">
            <span className="font-semibold">{testUsers.length} leftover test account{testUsers.length === 1 ? "" : "s"}</span> detected
            (internal seed data, e.g. delivered@resend.dev) — these are not real teammates.
          </p>
          <Button data-testid="cleanup-test-users" size="sm" className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white" onClick={cleanupTestUsers}>
            Remove {testUsers.length} test account{testUsers.length === 1 ? "" : "s"}
          </Button>
        </div>
      )}

      <NotificationDiagnostics />

      <div className="bg-white border border-zinc-200">
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto_auto] sm:grid-cols-[1fr_80px_95px_65px_65px_65px_65px_44px] gap-3 px-5 py-3 border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
          <span>Member</span>
          <span className="hidden sm:block">Role</span>
          <span>Manage</span>
          <span>Stats</span>
          <span>Clients</span>
          <span>Fees</span>
          <span>Leads</span>
          <span></span>
        </div>
        {members.map((m) => (
          <div key={m.user_id} className="grid grid-cols-[1fr_auto_auto_auto_auto_auto_auto] sm:grid-cols-[1fr_80px_95px_65px_65px_65px_65px_44px] gap-3 px-5 py-3.5 border-b border-zinc-100 last:border-0 items-center"
            data-testid={`member-row-${m.user_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              {(m.user_id === user.user_id || user.role === "admin") ? (
                <label className="relative cursor-pointer group shrink-0" title="Change photo" data-testid={`avatar-upload-${m.user_id}`}>
                  <Avatar id={m.user_id} name={m.name} size={34} version={avatarBust[m.user_id]} />
                  <span className="absolute inset-0 rounded-full bg-black/50 text-white text-[8px] font-semibold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">EDIT</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAvatar(m.user_id, e)} />
                </label>
              ) : (
                <Avatar id={m.user_id} name={m.name} size={34} />
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{m.name} {!m.active && <span className="text-[10px] text-amber-600 font-normal">· pending</span>}</p>
                <p className="text-xs text-zinc-500 truncate flex items-center gap-1.5">
                  <span className={m.email.endsWith("@twpco.placeholder") ? "text-amber-600" : ""}>
                    {m.email.endsWith("@twpco.placeholder") ? "no email yet — click to set" : m.email}
                  </span>
                  {user.role === "admin" && m.role !== "admin" && (
                    <button data-testid={`edit-email-${m.user_id}`} title="Edit email"
                      className="text-zinc-400 hover:text-zinc-900 transition-colors shrink-0"
                      onClick={() => setEmailEdit({ user_id: m.user_id, name: m.name, email: m.email.endsWith("@twpco.placeholder") ? "" : m.email })}>
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                </p>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-1.5">
              {m.role === "admin"
                ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-900"><Shield className="h-3.5 w-3.5" /> Admin</span>
                : <span className="inline-flex items-center gap-1 text-xs text-zinc-500"><UserCog className="h-3.5 w-3.5" /> Member</span>}
            </div>
            <div>
              {m.role === "admin" ? (
                <span className="text-xs text-zinc-400">Always</span>
              ) : (
                <Switch data-testid={`toggle-manage-${m.user_id}`} checked={m.can_manage} onCheckedChange={() => toggleManage(m)} />
              )}
            </div>
            <div>
              {m.role === "admin" ? (
                <span className="text-xs text-zinc-400">Always</span>
              ) : (
                <Switch data-testid={`toggle-stats-${m.user_id}`} checked={!!m.can_view_stats} onCheckedChange={() => toggleStats(m)} />
              )}
            </div>
            <div>
              {m.role === "admin" ? (
                <span className="text-xs text-zinc-400">Always</span>
              ) : (
                <Switch data-testid={`toggle-clients-${m.user_id}`} checked={!!m.can_view_clients} onCheckedChange={() => toggleClients(m)} />
              )}
            </div>
            <div>
              {m.role === "admin" ? (
                <span className="text-xs text-zinc-400">Always</span>
              ) : (
                <Switch data-testid={`toggle-fees-${m.user_id}`} checked={!!m.can_view_fees} onCheckedChange={() => toggleFees(m)} />
              )}
            </div>
            <div>
              {m.role === "admin" ? (
                <span className="text-xs text-zinc-400">Always</span>
              ) : (
                <Switch data-testid={`toggle-leads-${m.user_id}`} checked={!!m.can_view_leads} onCheckedChange={() => toggleLeads(m)} />
              )}
            </div>
            <div className="flex justify-end">
              {m.user_id !== user.user_id && m.role !== "admin" && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="text-zinc-400 hover:text-red-600 transition-colors" data-testid={`remove-member-${m.user_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-sm">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove {m.name}?</AlertDialogTitle>
                      <AlertDialogDescription>They will lose access to the studio workspace immediately.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                      <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => remove(m.user_id)}>Remove</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!emailEdit} onOpenChange={(o) => !o && setEmailEdit(null)}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit email — {emailEdit?.name}</DialogTitle>
            <DialogDescription>This must match the Google account they will sign in with.</DialogDescription>
          </DialogHeader>
          <Input data-testid="edit-email-input" type="email" placeholder="name@gmail.com"
            value={emailEdit?.email || ""} className="rounded-sm"
            onChange={(e) => setEmailEdit((s) => ({ ...s, email: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && saveEmail()} />
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEmailEdit(null)}>Cancel</Button>
            <Button data-testid="edit-email-save" className="rounded-sm" onClick={saveEmail}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
